import React, { createContext, useContext, useEffect, useState } from 'react';
import Userback from '@userback/widget';
import { environment_conf } from '../../utilities/environment';

interface UserbackContextType {
  userback: any | null;
  isLoaded: boolean;
  error: string | null;
  openFeedback: () => void;
  unminimizeWidget: () => void;
  closeWidget: () => void;
}

const UserbackContext = createContext<UserbackContextType | null>(null);

export const UserbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userback, setUserback] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let loadingTimeout: NodeJS.Timeout;
    let retryCount = 0;
    const maxRetries = 1;
    
    const init = async () => {
      try {
        if (!environment_conf.userback_access_token) {
          const errorMessage = 'Userback access token is not configured';
          setError(errorMessage);
          setIsLoaded(false);
          return;
        }
        
        const options = {
          on_load: () => {
            clearTimeout(loadingTimeout);
            setIsLoaded(true);
            setError(null);
          },
          on_open: () => {
          },
          on_close: () => {
          },
          widget_settings: {
            trigger: false,
            trigger_type: "api" as const,
          }
        };

        loadingTimeout = setTimeout(() => {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(() => {
              init();
            }, 1000);
          } else {
            setError('Widget failed to load after multiple attempts');
            setIsLoaded(false);
          }
        }, 10000);

        const userbackPromise = Userback(environment_conf.userback_access_token, options);
        
        if (userbackPromise && typeof userbackPromise.then === 'function') {
          userbackPromise.then((ub) => {
            setUserback(ub);
          }).catch((error) => {
            clearTimeout(loadingTimeout);
            
            if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(() => {
                init();
              }, 1000);
            } else {
              const errorMessage = error instanceof Error ? error.message : 'Promise rejected after multiple attempts';
              setError(errorMessage);
              setIsLoaded(false);
            }
          });
        } else {
          setUserback(userbackPromise);
        }
        
      } catch (error) {
        clearTimeout(loadingTimeout);
        
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(() => {
            init();
          }, 1000);
        } else {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error after multiple attempts';
          setError(errorMessage);
          setIsLoaded(false);
        }
      }
    };

    const timer = setTimeout(() => {
      init();
    }, 500);

    return () => {
      clearTimeout(timer);
      clearTimeout(loadingTimeout);
    };
  }, []);

  const isWidgetOpen = () => {
    const container = document.getElementById('userback_button_container');
    return container && container.getAttribute('isopen') === 'true';
  };

  const isWidgetMinimized = () => {
    const container = document.getElementById('userback_button_container');
    return container && 
           container.getAttribute('isopen') === 'true' && 
           container.getAttribute('minimized') === '1';
  };

  const unminimizeWidget = () => {
    const container = document.getElementById('userback_button_container');
    if (container && container.getAttribute('minimized') === '1') {
      container.removeAttribute('minimized');
    }
  };

  const minimizeWidget = () => {
    const container = document.getElementById('userback_button_container');
    if (container && container.getAttribute('minimized') !== '1') {
      container.setAttribute('minimized', '1');
    }
  };

  const openFeedback = () => {
    if (!isLoaded || !userback) {
      return;
    }
    
    try {
      if (isWidgetMinimized()) {
        unminimizeWidget();
      } else if (isWidgetOpen()) {
        minimizeWidget();
      } else {
        unminimizeWidget();
        userback.openForm('general', 'form');
      }
    } catch (error) {
    }
  };

  const closeWidget = () => {
    if (!isLoaded || !userback) {
      return;
    }
    
    try {
      const container = document.getElementById('userback_button_container');
      if (container && container.getAttribute('isopen') === 'true') {
        userback.close();
      }
    } catch (error) {
    }
  };



  return (
    <UserbackContext.Provider value={{ userback, isLoaded, error, openFeedback, unminimizeWidget, closeWidget }}>
      {children}
    </UserbackContext.Provider>
  );
};

export const useUserback = () => {
  const context = useContext(UserbackContext);
  if (!context) {
    throw new Error('useUserback must be used within a UserbackProvider');
  }
  return context;
}; 